import { API_ERROR_CODES } from '@solar/contracts';
import type { ApiErrorDetail } from '@solar/contracts';

/**
 * Every failure the API client can produce, normalised into one type.
 *
 * Transport failures, timeouts, unparseable bodies and structured API errors
 * all arrive here, so UI code branches on `code` rather than inspecting
 * `Response` objects or guessing at error shapes.
 */
export class ApiError extends Error {
  readonly code: string;
  readonly status: number;
  readonly details: readonly ApiErrorDetail[];
  readonly requestId?: string;

  constructor(params: {
    code: string;
    message: string;
    status: number;
    details?: readonly ApiErrorDetail[];
    requestId?: string;
  }) {
    super(params.message);
    this.name = 'ApiError';
    this.code = params.code;
    this.status = params.status;
    this.details = params.details ?? [];
    this.requestId = params.requestId;
  }

  /** True when retrying the same request could plausibly succeed. */
  get isRetryable(): boolean {
    return (
      this.code === API_ERROR_CODES.SERVICE_UNAVAILABLE ||
      this.code === NETWORK_ERROR_CODE ||
      this.code === TIMEOUT_ERROR_CODE ||
      this.status >= 500
    );
  }
}

/** The request never reached the API. */
export const NETWORK_ERROR_CODE = 'NETWORK_ERROR';

/** The request was abandoned after exceeding the configured timeout. */
export const TIMEOUT_ERROR_CODE = 'TIMEOUT_ERROR';

/** The API responded, but the body was not the expected JSON envelope. */
export const MALFORMED_RESPONSE_CODE = 'MALFORMED_RESPONSE';

/** Messages shown to users; deliberately free of technical detail. */
export const CLIENT_ERROR_MESSAGES = {
  [NETWORK_ERROR_CODE]: 'Unable to reach the server. Check your connection and try again.',
  [TIMEOUT_ERROR_CODE]: 'The server took too long to respond. Please try again.',
  [MALFORMED_RESPONSE_CODE]: 'The server returned an unexpected response.',
} as const;
